import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const AUTH_FLOW_LOGIN = "login";
const AUTH_FLOW_FORGOT_PASSWORD = "forgot-password";
const AUTH_FLOW_RESET_PASSWORD = "reset-password";

function PasswordField({
  label,
  value,
  onChange,
  onKeyDown,
  showValue,
  onToggleVisibility,
  autoComplete,
  sx,
}) {
  return (
    <TextField
      label={label}
      type={showValue ? "text" : "password"}
      fullWidth
      sx={sx}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={onKeyDown}
      autoComplete={autoComplete}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={onToggleVisibility} edge="end">
              {showValue ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

export default function Login({
  mode = AUTH_FLOW_LOGIN,
  resetToken = "",
  onLogin,
  onForgotPassword,
  onResetPassword,
  onNavigateToForgotPassword,
  onNavigateToLogin,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setError("");
    setSuccessMessage("");
    setSubmitting(false);
  }, [mode, resetToken]);

  const modeCopy = useMemo(() => {
    if (mode === AUTH_FLOW_FORGOT_PASSWORD) {
      return {
        title: "Forgot password",
        description:
          "Enter the email address used for your Vektor42 account. We will send a reset link if the account exists.",
      };
    }

    if (mode === AUTH_FLOW_RESET_PASSWORD) {
      return {
        title: "Reset password",
        description:
          "Choose a new password for your Vektor42 account. The reset link stays valid only for a short time.",
      };
    }

    return {
      title: "Login",
      description: "",
    };
  }, [mode]);

  const attemptLogin = async () => {
    setError("");
    setSuccessMessage("");
    setSubmitting(true);

    try {
      await onLogin({
        email: email.trim(),
        password,
      });
    } catch (err) {
      setError(err?.message || "Unable to log in with those credentials.");
    } finally {
      setSubmitting(false);
    }
  };

  const requestPasswordReset = async () => {
    setError("");
    setSuccessMessage("");
    setSubmitting(true);

    try {
      const payload = await onForgotPassword({
        email: email.trim(),
      });
      setSuccessMessage(
        payload?.message || "If the account exists, a password reset link has been sent."
      );
    } catch (err) {
      setError(err?.message || "Unable to send the password reset email.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitPasswordReset = async () => {
    if (!resetToken) {
      setError("This password reset link is invalid or incomplete.");
      return;
    }

    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError("New password and confirmation must match.");
      return;
    }

    setError("");
    setSuccessMessage("");
    setSubmitting(true);

    try {
      const payload = await onResetPassword({
        token: resetToken,
        newPassword,
      });
      setNewPassword("");
      setConfirmNewPassword("");
      setSuccessMessage(payload?.message || "Password updated. Please log in again.");
    } catch (err) {
      setError(err?.message || "Unable to reset the password.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderLoginForm = () => (
    <>
      <TextField
        label="Email"
        type="email"
        fullWidth
        sx={{ mb: 2 }}
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !submitting) {
            attemptLogin();
          }
        }}
        autoComplete="email"
      />

      <PasswordField
        label="Password"
        value={password}
        onChange={setPassword}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !submitting) {
            attemptLogin();
          }
        }}
        showValue={showPassword}
        onToggleVisibility={() => setShowPassword((current) => !current)}
        autoComplete="current-password"
        sx={{ mb: 2 }}
      />

      <Button
        variant="contained"
        fullWidth
        onClick={attemptLogin}
        disabled={submitting || !email.trim() || !password}
      >
        {submitting ? <CircularProgress size={20} color="inherit" /> : "Login"}
      </Button>

      <Box sx={{ mt: 1.5, textAlign: "right" }}>
        <Button
          variant="text"
          size="small"
          onClick={onNavigateToForgotPassword}
          disabled={submitting}
          sx={{ textTransform: "none", px: 0 }}
        >
          Forgot password?
        </Button>
      </Box>
    </>
  );

  const renderForgotPasswordForm = () => (
    <>
      <Typography sx={{ mb: 2, fontSize: "0.92rem", color: "#475569" }}>
        {modeCopy.description}
      </Typography>

      <TextField
        label="Email"
        type="email"
        fullWidth
        sx={{ mb: 2 }}
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !submitting) {
            requestPasswordReset();
          }
        }}
        autoComplete="email"
      />

      <Button
        variant="contained"
        fullWidth
        onClick={requestPasswordReset}
        disabled={submitting || !email.trim()}
      >
        {submitting ? <CircularProgress size={20} color="inherit" /> : "Send reset link"}
      </Button>

      <Box sx={{ mt: 1.5, textAlign: "center" }}>
        <Button
          variant="text"
          size="small"
          onClick={onNavigateToLogin}
          disabled={submitting}
          sx={{ textTransform: "none" }}
        >
          Back to login
        </Button>
      </Box>
    </>
  );

  const renderResetPasswordForm = () => {
    if (!resetToken) {
      return (
        <>
          <Alert severity="error" sx={{ mb: 2 }}>
            This password reset link is invalid or incomplete.
          </Alert>
          <Button variant="contained" fullWidth onClick={onNavigateToLogin}>
            Back to login
          </Button>
        </>
      );
    }

    if (successMessage) {
      return (
        <Button variant="contained" fullWidth onClick={onNavigateToLogin}>
          Back to login
        </Button>
      );
    }

    return (
      <>
        <Typography sx={{ mb: 2, fontSize: "0.92rem", color: "#475569" }}>
          {modeCopy.description}
        </Typography>

        <PasswordField
          label="New password"
          value={newPassword}
          onChange={setNewPassword}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !submitting) {
              submitPasswordReset();
            }
          }}
          showValue={showNewPassword}
          onToggleVisibility={() => setShowNewPassword((current) => !current)}
          autoComplete="new-password"
          sx={{ mb: 2 }}
        />

        <PasswordField
          label="Confirm new password"
          value={confirmNewPassword}
          onChange={setConfirmNewPassword}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !submitting) {
              submitPasswordReset();
            }
          }}
          showValue={showConfirmNewPassword}
          onToggleVisibility={() => setShowConfirmNewPassword((current) => !current)}
          autoComplete="new-password"
          sx={{ mb: 2 }}
        />

        <Button
          variant="contained"
          fullWidth
          onClick={submitPasswordReset}
          disabled={submitting || !newPassword || !confirmNewPassword}
        >
          {submitting ? <CircularProgress size={20} color="inherit" /> : "Reset password"}
        </Button>

        <Box sx={{ mt: 1.5, textAlign: "center" }}>
          <Button
            variant="text"
            size="small"
            onClick={onNavigateToLogin}
            disabled={submitting}
            sx={{ textTransform: "none" }}
          >
            Back to login
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f0f2f5",
        px: 2,
      }}
    >
      <Paper sx={{ p: 4, width: "100%", maxWidth: 400, textAlign: "center" }}>
        <Typography variant="h5" sx={{ mb: modeCopy.description ? 1.5 : 2, fontWeight: 700 }}>
          {modeCopy.title}
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
            {error}
          </Alert>
        ) : null}

        {successMessage ? (
          <Alert severity="success" sx={{ mb: 2, textAlign: "left" }}>
            {successMessage}
          </Alert>
        ) : null}

        {mode === AUTH_FLOW_FORGOT_PASSWORD
          ? renderForgotPasswordForm()
          : mode === AUTH_FLOW_RESET_PASSWORD
          ? renderResetPasswordForm()
          : renderLoginForm()}
      </Paper>
    </Box>
  );
}
